/*
 * Zugabe-Motor der Aktion.
 *
 * Zwei Teile in einer Datei:
 * 1. Ein reiner Logik-Kern (countLamps/active/fingerprint/plan), der nur den
 *    JSON-Zustand aus snippets/moonou-promo-cart-state.liquid kennt. Er laeuft
 *    ueber module.exports auch in Node und ist deshalb testbar.
 * 2. Die Browser-Klasse PromoGift, die den Zustand liest, den Warenkorb
 *    abgleicht und den Checkout so lange anhaelt, bis der Abgleich steht.
 *
 * Der Motor kennt keine Produkttypen und keine Collections. Die Einordnung
 * jeder Warenkorbzeile (kind, units, paid, staffel, pickUnits) passiert in Liquid.
 *
 * Zwei Spuren:
 * - Hauptaktion bzw. Vase des Angebots (state.key): legt die Zugabe selbst zu
 *   und raeumt sie wieder ab.
 * - Gratis-Tischlampe des Angebots (state.pick): legt NIE selbst zu, das macht
 *   der Auswahl-Popup (assets/moonou-gift-picker.js). Der Motor raeumt nur ab:
 *   Zugaben ueber der berechtigten Stueckzahl, ausserhalb des Zeitfensters,
 *   bei ausgeschalteter Spur und bezahlte Zugaben. Siehe planPick().
 *   state.pick liefert nur snippets/moonou-angebot-state.liquid; im Zustand
 *   aus snippets/moonou-promo-cart-state.liquid fehlt es (Live-Aktion 2
 *   rechnet Shopify per Buy-X-Get-Y ab), dann tut dieser Teil nichts.
 *
 * Nach dem Angebot (Feld aktiv aus, Ende ueberschritten) gibt
 * snippets/moonou-angebot-state.liquid nur noch einen Zustand zum Abraeumen
 * aus (abraeumen, cleanupKeys), solange Geschenke des Angebots im Warenkorb
 * liegen. Der Motor entfernt sie still und laesst Ticket und Live-Aktionen in
 * Ruhe. Siehe isCleanupGift() und read().
 */
(function (root) {
  'use strict';

  /*
   * Laufzeit-Kennung fuer pubsub und Web-Lock. Bewusst KONSTANT und unabhaengig
   * von settings.promo_key: waere sie leer, koennte der Selbst-Trigger-Schutz
   * kippen und eine Endlosschleife aus Warenkorb-Events entstehen.
   */
  const CHANNEL = 'moonou-promo-gift';

  /*
   * Rechenkern des Angebots „Mengenstaffel" (assets/moonou-angebot.js). In Node
   * per require, im Browser window.MoonouAngebot (layout/theme.liquid laedt ihn
   * VOR diesem Skript). Fehlt er, legt die Regel 'staffel' nie etwas zu: eine
   * Zugabe zu verschenken, weil Code fehlt, ist der teurere Fehler.
   */
  const angebotLib = () => ((typeof module !== 'undefined' && module.exports)
    ? require('./moonou-angebot.js')
    : root.MoonouAngebot);

  /* Die eigene Zugabe der laufenden Aktion. Key-abhaengig. */
  const isGift = (item, key) => !!key && item.gift === key;

  /*
   * Zugabe einer frueheren Aktion: dasselbe Geschenk, aber mit alter Kennung.
   * Wird aufgeraeumt, damit ein Wechsel von promo_key keine Altlast im Warenkorb
   * liegen laesst, die weder zaehlt noch rabattiert ist.
   *
   * Bewusst auf die aktuelle Geschenkvariante beschraenkt: der Gift-Picker setzt
   * ebenfalls ein _gift-Merkmal. Ein pauschales Aufraeumen aller fremden
   * Kennungen wuerde dessen Zugaben mit loeschen.
   */
  const isStaleGift = (item, key, giftId) => !!key && !!item.gift && item.gift !== key && item.variantId === giftId;

  /*
   * Gekauft heisst: bezahlt, keine Zugabe (egal welcher Aktion) und kein
   * Gratis-Artikel aus dem Bundle-Konfigurator. Bewusst key-UNabhaengig.
   */
  const isPurchased = (item) => item.paid && !item.gift && item.bundleRole !== 'gratis';

  const minUnits = (state) =>
    Number.isInteger(state.minLamps) && state.minLamps >= 1 && state.minLamps <= 10 ? state.minLamps : 2;

  function countLamps(items) {
    let count = 0;
    const assemblies = new Map();
    for (const item of items) {
      if (!isPurchased(item)) continue;
      /* Die 2-Stueck-Variante traegt units = 2 und zaehlt als zwei Lampen. */
      if (item.kind === 'lamp') count += item.quantity * item.units;
      if (item.kind !== 'base' && item.kind !== 'shade') continue;
      /* Sockel + Schirm derselben Gruppe ergeben zusammen eine Lampe. */
      const group = item.group || 'legacy';
      if (!assemblies.has(group)) assemblies.set(group, { base: 0, shade: 0 });
      assemblies.get(group)[item.kind] += item.quantity;
    }
    for (const pair of assemblies.values()) count += Math.min(pair.base, pair.shade);
    return count;
  }

  /*
   * Schwelle des Mindest-Bestellwerts in Cent. Ein unbrauchbarer Wert (fehlt,
   * keine Zahl, kleiner 1) heisst NIE erfuellt: eine Zugabe zu verschenken,
   * weil eine Zahl fehlt, ist der teurere Fehler. Wortgleiche Regel im
   * Auswahl-Pfad, assets/moonou-gift-picker.js, minSubtotalOf().
   */
  const minSubtotal = (state) => {
    const value = Number(state.minSubtotal);
    return Number.isFinite(value) && value >= 1 ? value : Infinity;
  };

  /*
   * Zaehlt eine Zeile fuer den Bestellwert? Alles ausser der eigenen Zugabe und
   * Gratis-Zeilen aus dem Bundle-Konfigurator.
   *
   * Die eigene Zugabe MUSS draussen bleiben: zaehlte sie mit, hielte sie sich
   * nach dem Hinzufuegen selbst ueber der Schwelle. Ein Warenkorb, der unter
   * die Schwelle faellt, kaeme dann nie wieder heraus - die Zugabe bliebe
   * liegen, Shopify stellte sie nicht mehr gratis, und der Motor erkennt
   * genau das als unpaidDiscount und zeigt dem Kunden einen Fehler.
   *
   * Bewusst NICHT auf isPurchased() gestuetzt: der Bestellwert fragt nicht
   * nach Lampen, sondern nach Geld. Zubehoer, Sockel und Produkte ausserhalb
   * Gezaehlt werden nur die AKTIONSPRODUKTE, also die Zeilen, die der
   * Warenkorb-Zustand als Lampe, Sockel oder Schirm eingeordnet hat
   * (kind != 'other'). Grund: Shopifys BXGY-Regel misst ihre Kaufbedingung
   * ebenfalls nur an den ausgewaehlten 24 Aktionsprodukten. Zaehlte das Theme
   * mehr (etwa Vasen oder Leuchtmittel), haelte es einen Warenkorb fuer
   * berechtigt, den Shopify nicht rabattiert - die Zugabe wuerde hinzugefuegt
   * und sofort wieder entfernt. Genau dieser Fehler trat am 13.09.2026 auf.
   */
  const countsForSubtotal = (item, key) =>
    !isGift(item, key) && item.bundleRole !== 'gratis' && item.kind !== 'other';

  function cartSubtotal(items, key) {
    let total = 0;
    for (const item of items) {
      if (!countsForSubtotal(item, key)) continue;
      const price = Number(item.price);
      if (Number.isFinite(price) && price > 0) total += price;
    }
    return total;
  }

  /*
   * Die eine Stelle, an der die Bedingung der Aktion beantwortet wird. Beide
   * Regeln liegen hier zusammen, damit plan() sie nicht auseinanderziehen kann;
   * eine unbekannte Regel faellt auf die Lampen-Zaehlung zurueck (Stand vor
   * dieser Erweiterung).
   */
  function qualifies(state) {
    if (state.rule === 'subtotal') return cartSubtotal(state.items, state.key || '') >= minSubtotal(state);
    if (state.rule === 'staffel') {
      /* Angebot Mengenstaffel: dieselbe Zaehlung wie Drawer und Rabatt
         (staffel-Merkmal aus snippets/moonou-angebot-state.liquid). */
      const lib = angebotLib();
      return !!lib && lib.giftEligible(lib.countStaffel(state.items), state.minLamps);
    }
    return countLamps(state.items) >= minUnits(state);
  }

  function active(state, now = Date.now()) {
    if (!state.enabled) return false;
    const start = state.start ? Date.parse(state.start) : -Infinity;
    const end = state.end ? Date.parse(state.end) : Infinity;
    /* Unlesbares Datum ergibt NaN und damit false: im Zweifel Aktion aus. */
    return start <= now && now < end;
  }

  /* --- Spur 2: Gratis-Tischlampe ------------------------------------------- */

  /* Zeitfenster. Leeres Ende = unbefristet, leerer Start = sofort. */
  function inWindow(zeitraum, now) {
    const start = zeitraum.start ? Date.parse(zeitraum.start) : -Infinity;
    const end = zeitraum.end ? Date.parse(zeitraum.end) : Infinity;
    /* Unlesbares Datum ergibt NaN und damit false: im Zweifel Spur aus. */
    return start <= now && now < end;
  }

  /*
   * Kennung der Gratis-Tischlampe. Bewusst unabhaengig von pick.enabled: auch
   * bei ausgeschalteter Spur muss der Motor ihre Zeilen noch erkennen, um sie
   * abzuraeumen (planPick gibt dann keine Berechtigung).
   */
  const pickKey = (state) => (state.pick && typeof state.pick.key === 'string' && state.pick.key.trim()) || '';

  const isPickGift = (item, state) => {
    const key = pickKey(state);
    return !!key && item.gift === key;
  };

  /*
   * Wie viele Gratis-Tischlampen stehen zu? Jede gekaufte Einheit einer
   * Deckenlampe eine (pickUnits aus Liquid). Gekauft heisst wie ueberall:
   * bezahlt, keine Zugabe (egal welcher Spur), kein Bundle-Gratisartikel. Eine
   * Gratis-Tischlampe berechtigt also nie selbst.
   */
  function countPickAllowance(items) {
    let count = 0;
    for (const item of items) {
      if (!isPurchased(item)) continue;
      const units = Number(item.pickUnits);
      if (Number.isFinite(units) && units > 0) count += item.quantity * units;
    }
    return count;
  }

  /*
   * Aufraeumen fuer Spur 2. Reihenfolge je Zugabe-Zeile:
   * 1. keine Berechtigung (mehr), Spur ausgeschaltet oder ausserhalb des
   *    Zeitfensters -> entfernen, still;
   * 2. mehr Stueck als berechtigt -> auf die Berechtigung kuerzen, der Preis
   *    wird erst im naechsten Durchlauf mit der neuen Menge geprueft;
   * 3. bezahlt, obwohl berechtigt -> die 0-EUR-Variante greift nicht. Entfernen
   *    und melden (unpaidPick), statt den Kunden zahlen zu lassen.
   */
  function planPick(state, now = Date.now()) {
    const updates = {};
    let unpaidPick = false;
    const key = pickKey(state);
    if (!key) return { updates, unpaidPick };
    const live = !!state.pick.enabled && inWindow(state.pick, now);
    let allowance = live ? countPickAllowance(state.items) : 0;
    for (const gift of state.items) {
      if (gift.gift !== key) continue;
      if (allowance <= 0) {
        updates[gift.key] = 0;
      } else if (gift.quantity > allowance) {
        updates[gift.key] = allowance;
        allowance = 0;
      } else if (gift.paid) {
        updates[gift.key] = 0;
        unpaidPick = true;
      } else {
        allowance -= gift.quantity;
      }
    }
    return { updates, unpaidPick };
  }

  /*
   * Geschenk einer frueheren Aktion (Kennung in state.legacyKeys, z. B.
   * herbst-vase-2026 auf der alten Round-Variante). Nach dem Go-Live stellt
   * Shopify solche Zeilen nicht mehr gratis: still entfernen, ohne Meldung.
   * Eigene Kennungen (Vase, Gratis-Tischlampe) sind nie alt.
   */
  const isLegacyGift = (item, state) => Array.isArray(state.legacyKeys) && !!item.gift
    && item.gift !== (state.key || '') && item.gift !== pickKey(state)
    && state.legacyKeys.includes(item.gift);

  /*
   * Geschenk eines beendeten Angebots (Kennung in state.cleanupKeys: Vase,
   * Gratis-Tischlampe, Bundle-Geschenke). Die Liste fuellt
   * snippets/moonou-angebot-state.liquid nur bei Angebot aus. Es sind
   * 0-EUR-Varianten: blieben sie liegen, gingen sie gratis durch den
   * Checkout. Deshalb unabhaengig von state.enabled still entfernen.
   */
  const isCleanupGift = (item, state) => Array.isArray(state.cleanupKeys) && !!item.gift
    && item.gift !== (state.key || '') && item.gift !== pickKey(state)
    && state.cleanupKeys.includes(item.gift);

  /*
   * Stabiler Fingerabdruck aller gekauften Zeilen. Dient nur dazu zu erkennen,
   * dass der Kunde die Zugabe selbst entfernt hat, ohne sonst etwas zu aendern.
   */
  function fingerprint(state) {
    return JSON.stringify(state.items.filter((item) => isPurchased(item) && item.kind !== 'other')
      .map((item) => [item.variantId, item.quantity, item.kind, item.units, item.group])
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  }

  function plan(state, suppressed = false, now = Date.now()) {
    const key = state.key || '';
    const gifts = state.items.filter((item) => isGift(item, key));
    /* Ohne Kennung wird nie zugelegt: eine Zugabe ohne _gift waere spaeter nicht wiederzuerkennen. */
    const eligible = !!key && active(state, now) && state.autoAdd && state.available
      && qualifies(state);
    /* Spur 2 zuerst: ihre Zeilen tragen eine andere Kennung und kreuzen sich
       mit der Vase nie (isGift/isStaleGift pruefen die Kennung bzw. die
       Geschenkvariante der Vase). */
    const pickStep = planPick(state, now);
    const updates = { ...pickStep.updates };
    const unpaidPick = pickStep.unpaidPick;
    let kept = false;
    let unpaidDiscount = false;
    /* Altlasten frueherer Aktionen raeumen, solange die Aktion ueberhaupt laeuft. */
    if (state.enabled) {
      for (const item of state.items) {
        if (isStaleGift(item, key, state.giftId) || isLegacyGift(item, state)) updates[item.key] = 0;
      }
    }
    for (const item of state.items) {
      if (isCleanupGift(item, state)) updates[item.key] = 0;
    }
    for (const gift of gifts) {
      if (eligible && gift.variantId === state.giftId && gift.quantity > 1 && !kept) {
        kept = true;
        updates[gift.key] = 1;
      } else if (eligible && gift.variantId === state.giftId && !gift.paid && !kept) {
        kept = true;
        if (gift.quantity !== 1) updates[gift.key] = 1;
      } else {
        updates[gift.key] = 0;
        /* Bezahlte Zugabe heisst: die Rabattregel greift nicht. Aufraeumen statt kassieren. */
        if (eligible && gift.paid) unpaidDiscount = true;
      }
    }
    if (Object.keys(updates).length) return { type: 'update', updates, unpaidDiscount, unpaidPick };
    if (eligible && !kept && !suppressed) return { type: 'add' };
    return { type: 'none' };
  }

  const logic = { CHANNEL, isGift, isStaleGift, isLegacyGift, isCleanupGift, isPurchased, countLamps, cartSubtotal, qualifies, active, fingerprint, plan, planPick, countPickAllowance, isPickGift };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = logic;
    return;
  }
  /*
   * ACHTUNG, teuer gelernt am 13.09.2026: Dieser Name darf NIE mit einer
   * DOM-id im Theme uebereinstimmen. Browser legen fuer jedes Element mit id
   * automatisch eine gleichnamige window-Eigenschaft an. Das Zugabe-Popup
   * heisst id="MoonouPromoGift" - hiess der Schutz ebenso, fand der Motor
   * beim Start das Modal vor, hielt sich fuer bereits initialisiert und
   * brach ab. Ohne Fehlermeldung, ohne Konsolenausgabe: die Zugabe wurde
   * schlicht nie hinzugefuegt. Der Unterstrich-Praefix haelt den Namen aus
   * dem id-Raum heraus; tests/promo-gift.test.cjs sichert das ab.
   */
  if (root.__moonouPromoGiftEngine) return;

  /*
   * Live-Zustand (data-promo-cart-state), aber nur bei „Zugabe automatisch"
   * (mode 'gift', im Angebot ebenfalls 'gift'). Den Zustand von „Zugabe zur
   * Auswahl" rendert der Gift-Picker fuer sich; dessen Zugaben darf der Motor
   * nie anfassen, er waere dort sonst auch nie geladen.
   */
  const liveElement = (doc) => {
    const element = doc.querySelector('[data-promo-cart-state]');
    if (!element) return null;
    const mode = JSON.parse(element.textContent).mode;
    return mode && mode !== 'gift' ? null : element;
  };

  /* Nichts zu tun: nach dem Abraeumen, wenn kein eigener Zustand mehr da ist. */
  const ruhe = () => ({ abraeumen: true, key: '', enabled: false, autoAdd: false, items: [] });

  class PromoGift {
    constructor() {
      /* Nur zum Abraeumen geladen (Angebot aus, kein Live-Motor auf der Seite)? */
      this.nurAbraeumen = !liveElement(document);
      this.state = this.read(document);
      this.running = null;
      this.dirty = false;
      this.previous = null;
      this.suppressed = '';
      this.failed = '';
      this.pickFailed = false;
      this.bypassCheckout = false;
      this.status = document.createElement('p');
      this.status.className = 'sr-only';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      document.body.append(this.status);
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== CHANNEL) this.sync();
      });
      document.addEventListener('click', (event) => this.checkout(event), true);
      document.addEventListener('submit', (event) => this.checkout(event), true);
      document.addEventListener('shopify:section:load', () => this.updateMode());
      window.addEventListener('pageshow', () => this.sync());
      window.addEventListener('focus', () => this.sync());
      this.updateMode();
      if (this.needsSync()) this.sync();
      setInterval(() => {
        this.updateMode();
        if (!document.hidden && this.needsSync()) this.sync();
      }, 30000);
    }

    /* Eigene Zugabe einer der beiden Spuren im Warenkorb? */
    hasOwnGift(state = this.state) {
      return state.items.some((item) => isGift(item, state.key) || isPickGift(item, state));
    }

    /* Nur abgleichen, wenn es etwas zu tun geben kann: automatisches Zulegen,
       eine eigene Zugabe (Vase oder Gratis-Tischlampe), ein Alt-Geschenk oder
       ein Geschenk des beendeten Angebots im Korb. */
    needsSync(state = this.state) {
      return !!state.autoAdd || this.hasOwnGift(state)
        || (!!state.enabled && state.items.some((item) => isLegacyGift(item, state)))
        || state.items.some((item) => isCleanupGift(item, state));
    }

    /*
     * Welcher Zustand gilt? Zuerst der des Angebots (data-angebot-state; im
     * Angebot zugleich data-promo-cart-state, danach nur zum Abraeumen), sonst
     * der Live-Zustand. Ist nach dem Abraeumen keiner mehr da und war der
     * Motor nur dafuer geladen, ruht er.
     */
    read(doc) {
      const element = doc.querySelector('[data-angebot-state]') || liveElement(doc);
      if (!element) {
        if (this.nurAbraeumen) return ruhe();
        throw new Error('Promo-Warenkorbstatus fehlt');
      }
      const state = JSON.parse(element.textContent);
      if (!Array.isArray(state.items)) throw new Error('Promo-Warenkorbstatus unvollstaendig');
      return state;
    }

    /*
     * Das Ticket bleibt im HTML und wird nur per Klasse versteckt: Liquids `now`
     * ist wegen Page-Caching unzuverlaessig, das Zeitfenster entscheidet der Browser.
     * Der Lampenschirm-Modus spielt keine Rolle: Das Ticket bleibt auch bei
     * "Nur Lampenschirm" sichtbar (R10).
     */
    updateMode() {
      /* Abraeum-Zustand: Ticket gehoert der Live-Aktion, nicht anfassen. */
      if (this.state.abraeumen) return;
      document.querySelectorAll('[data-promo-ticket]').forEach((ticket) => {
        ticket.classList.toggle('moonou-promo-inactive', !active(this.state) || !this.state.available);
      });
    }

    async load(url) {
      const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Warenkorbstatus nicht erreichbar');
      return this.read(new DOMParser().parseFromString(await response.text(), 'text/html'));
    }

    /*
     * Der Warenkorbstatus ist ein Snippet im Layout, keine Section. Die Section
     * Rendering API rendert ausschliesslich Sections, deshalb holen wir die
     * kleinste Seite, die das Layout mitbringt: die Warenkorbseite. Faellt die
     * aus (eigenes Layout, Weiterleitung), greift die aktuelle Seite.
     */
    async fresh() {
      try {
        return await this.load(new URL(theme.routes.cart_url, location.origin));
      } catch (error) {
        return this.load(new URL(location.pathname, location.origin));
      }
    }

    async post(route, data) {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true, detail: { sections }
      }));
      const response = await fetch(route, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...data, sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) throw new Error(result.description || 'Warenkorb-Aenderung fehlgeschlagen');
      return result;
    }

    async publish(result) {
      const response = await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'));
      if (!response.ok) throw new Error('Warenkorb-Aktualisierung fehlgeschlagen');
      const cart = await response.json();
      cart.sections = result.sections;
      if (cart.sections && Object.values(cart.sections).every((html) => typeof html === 'string')) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: CHANNEL, cart });
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      }
    }

    async reconcile() {
      let state = await this.fresh();
      let result = null;
      let added = false;
      const stamp = fingerprint(state);
      /* Zugabe von Hand entfernt, sonst nichts geaendert: nicht sofort nachlegen. */
      if (this.previous && this.previous.stamp === stamp && this.previous.hadGift && !this.hasOwnGift(state)) {
        this.suppressed = stamp;
      }
      for (let attempt = 0; attempt < 5; attempt++) {
        const step = plan(state, this.suppressed === stamp || this.failed === stamp);
        if (step.type === 'none') {
          this.state = state;
          this.updateMode();
          this.previous = { stamp: fingerprint(state), hadGift: this.hasOwnGift(state) };
          if (result) await this.publish(result);
          if (added && state.items.some((item) => isGift(item, state.key) && !item.paid)) {
            this.status.textContent = state.addedMessage;
          }
          if (this.pickFailed) {
            this.pickFailed = false;
            this.showError((state.pick && state.pick.errorMessage) || state.errorMessage);
          }
          if (this.failed === stamp) this.showError();
          return;
        }
        if (step.type === 'update') {
          if (step.unpaidDiscount) this.failed = stamp;
          if (step.unpaidPick) this.pickFailed = true;
          result = await this.post(theme.routes.cart_update_url, { updates: step.updates });
        } else {
          result = await this.post(theme.routes.cart_add_url, {
            items: [{ id: state.giftId, quantity: 1, properties: { _gift: state.key } }]
          });
          added = true;
        }
        state = await this.fresh();
      }
      throw new Error('Warenkorb kam nicht zur Ruhe');
    }

    sync() {
      if (!this.needsSync()) return Promise.resolve(true);
      this.dirty = true;
      if (this.running) return this.running;
      this.running = (async () => {
        try {
          while (this.dirty) {
            this.dirty = false;
            if (navigator.locks) await navigator.locks.request(CHANNEL, () => this.reconcile());
            else await this.reconcile();
          }
          return true;
        } catch (error) {
          console.error('[Moonou Aktion]', error);
          this.showError();
          return false;
        } finally {
          this.running = null;
        }
      })();
      return this.running;
    }

    showError(text = this.state.errorMessage) {
      if (!text) return;
      this.status.textContent = text;
      document.querySelectorAll('[name="checkout"]').forEach((button) => {
        const parent = button.parentElement;
        let message = parent.querySelector('[data-promo-cart-error]');
        if (!message) {
          message = document.createElement('p');
          message.className = 'moonou-promo-cart-message';
          message.setAttribute('data-promo-cart-error', '');
          message.setAttribute('role', 'status');
          button.before(message);
        }
        message.textContent = text;
      });
    }

    /*
     * Haelt den Checkout an, bis der Warenkorb abgeglichen ist. Steigt sofort
     * aus, wenn weder automatisch zugelegt wird noch eine Zugabe im Warenkorb
     * liegt: eine reine Darstellungs-Aktion fasst den Checkout nie an.
     */
    async checkout(event) {
      if (this.bypassCheckout || !this.needsSync()) return;
      const target = event.type === 'submit' ? event.submitter : event.target.closest('[name="checkout"], a[href]');
      if (!target || (target.name !== 'checkout' && !/\/checkout(?:[/?#]|$)/.test(target.getAttribute('href') || ''))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.checkingOut) return;
      this.checkingOut = true;
      const success = await this.sync();
      this.checkingOut = false;
      if (!success) return;
      this.bypassCheckout = true;
      const replacement = document.querySelector('[name="checkout"]');
      const button = target.isConnected ? target : replacement;
      if (button?.form) button.form.requestSubmit(button);
      else if (target.href) location.assign(target.href);
      this.bypassCheckout = false;
    }
  }

  const init = () => {
    if (!document.querySelector('[data-angebot-state]') && !document.querySelector('[data-promo-cart-state]')) return;
    root.__moonouPromoGiftEngine = new PromoGift();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})(typeof window !== 'undefined' ? window : globalThis);
